"use client";

/**
 * What our storage efficiency actually buys, at a capacity the reader picks.
 *
 * WHY INTERACTIVE. "21.8 KB per page" is a true figure that means nothing to most readers. Kilobytes
 * are not a unit anyone has intuition for. Let someone drag a slider to "1 TB" and read back
 * "49 million pages, about 138,000 sites" and the efficiency claim lands as a quantity they can
 * picture. The comparison bar does the rest: the same drive holds roughly eight times fewer pages at
 * the reference rate.
 *
 * THE REFERENCE POINT IS DELIBERATELY NAMED ONLY AS "CC" in anything a visitor reads — the full
 * name is not on the page. The maths is stated openly so anyone can check it. Two honesty notes are
 * load-bearing here:
 *
 *  1. IT IS NOT A LIKE-FOR-LIKE COMPARISON, and the footnote says so. The reference is a raw
 *     archive of fetched pages; we store processed, searchable passages plus their vectors. Those
 *     are different artefacts serving different purposes, and the reason the numbers differ is
 *     partly that difference rather than pure engineering merit. Presenting it as a clean win over
 *     a competitor would be misleading — it is a public archive, not a rival, and it is used here
 *     because it is the only web-scale per-page storage figure anybody can independently verify.
 *
 *  2. OUR NUMBER IS LIVE, THEIRS IS FIXED. bytes_per_page_disk comes from the running system, so the
 *     ratio recomputes itself and cannot drift into a lie the way a hardcoded "8.4×" would. The CC
 *     figure is a constant with its source arithmetic recorded beside it.
 *
 * Pages-per-domain also comes from the live endpoint, so "how many sites" tracks how deeply we
 * actually read rather than an assumption someone made once.
 */

import { useEffect, useMemo, useState } from "react";
import { HardDrive } from "lucide-react";
import { fmtCount } from "@/lib/format-stat";

const STORAGE_URL = "/api/index/storage";
const STATS_URL = "/api/index/stats";

/**
 * CC archive scale: 364.01 TiB across 2.14 billion pages.
 *   364.01 × 1024^4 ÷ 2,140,000,000 = 187,025 bytes = 182.64 KB per page.
 * Recorded as bytes so the component never re-derives it from a rounded KB value.
 */
/* EXPORTED so /scale's efficiency card compares against the same rate this page does. Two copies
   of a reference figure is how two pages of the same site end up quoting different ratios. */
export const CC_BYTES_PER_PAGE = 187_025;
export const CC_LABEL = "182.6 KB";

/**
 * Capacities a reader can actually picture, rather than a continuous byte slider.
 *
 * The ladder runs past what one drive holds on purpose. A laptop SSD makes the per-page figure
 * concrete; the rack-scale end answers the question a technical reader asks straight afterwards —
 * "what would it take to hold a serious fraction of the web?" — and at 1.2 PB the answer is a number
 * that reads as an ordinary infrastructure line rather than a moonshot. That contrast IS the section's
 * argument, so the range has to reach far enough to make it.
 */
const CAPACITIES = [
  { label: "1 GB", bytes: 1024 ** 3 },
  { label: "10 GB", bytes: 10 * 1024 ** 3 },
  { label: "100 GB", bytes: 100 * 1024 ** 3 },
  { label: "1 TB", bytes: 1024 ** 4 },
  { label: "10 TB", bytes: 10 * 1024 ** 4 },
  { label: "30 TB", bytes: 30 * 1024 ** 4 },
  { label: "90 TB", bytes: 90 * 1024 ** 4 },
  { label: "300 TB", bytes: 300 * 1024 ** 4 },
  { label: "1.2 PB", bytes: 1.2 * 1024 ** 5 },
];

export function StorageScaleExplorer(
  { initial }: { initial?: { perPage?: number | null; perDomain?: number | null } | null },
) {
  /* Seeded by the server so the figure is in the HTML on first paint. Before this the value
     arrived only after hydration, so every visitor saw a dash first and anything that does not
     run JavaScript — crawlers, link previews — never saw it at all. The client fetch below is
     kept: it refreshes a page that may have been cached for minutes. */
  const [perPage, setPerPage] = useState<number | null>(initial?.perPage ?? null);
  const [perDomain, setPerDomain] = useState<number | null>(initial?.perDomain ?? null);
  const [i, setI] = useState(3); // default to 1 TB — the capacity most people own something of

  useEffect(() => {
    let on = true;
    Promise.all([
      fetch(STORAGE_URL, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)),
      fetch(STATS_URL, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([st, stats]) => {
        if (!on) return;
        const bpp = st?.web?.bytes_per_page_disk;
        if (bpp > 0) setPerPage(bpp);
        const d = stats?.database?.total_domains;
        const p = stats?.database?.total_indexed_pages;
        if (d > 0 && p > 0) setPerDomain(p / d);
      })
      .catch(() => {});
    return () => { on = false; };
  }, []);

  const cap = CAPACITIES[i];

  const view = useMemo(() => {
    if (!perPage) return null;
    const ours = Math.floor(cap.bytes / perPage);
    const theirs = Math.floor(cap.bytes / CC_BYTES_PER_PAGE);
    return {
      ours,
      theirs,
      domains: perDomain ? Math.floor(ours / perDomain) : null,
      ratio: CC_BYTES_PER_PAGE / perPage,
      reduction: (1 - perPage / CC_BYTES_PER_PAGE) * 100,
      perPageKb: perPage / 1024,
      // Bar widths are relative to the larger count, which is always ours.
      theirsPct: (theirs / ours) * 100,
    };
  }, [perPage, perDomain, cap]);

  return (
    <div className="overflow-hidden rounded-2xl border hairline-strong bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-6 py-4">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-foreground/50">
          What a drive holds
        </span>
        {view && (
          <span className="font-mono text-xs text-foreground/45">
            {view.ratio.toFixed(1)}× more per byte · {Math.round(view.reduction)}% smaller
          </span>
        )}
      </div>

      <div className="p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-2">
          <HardDrive className="h-4 w-4 text-foreground/40" aria-hidden />
          <span className="mr-1 text-xs text-foreground/55">Capacity</span>
          {CAPACITIES.map((c, idx) => (
            <button
              key={c.label}
              type="button"
              onClick={() => setI(idx)}
              aria-pressed={idx === i}
              className={[
                "rounded-full border px-3 py-1 font-mono text-xs transition-colors",
                idx === i
                  ? "hairline-strong bg-foreground text-background"
                  : "hairline text-foreground/65 hover:bg-foreground/5",
              ].join(" ")}
            >
              {c.label}
            </button>
          ))}
        </div>

        {!view ? (
          <p className="mt-8 text-sm text-foreground/45">Reading live figures…</p>
        ) : (
          <>
            <div className="mt-8 space-y-5">
              <div>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">askFinz</span>
                  <span className="font-mono text-sm">
                    <span className="text-lg font-semibold">{fmtCount(view.ours)}</span>
                    <span className="text-foreground/50"> pages</span>
                  </span>
                </div>
                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-foreground/10">
                  <div className="h-full w-full rounded-full bg-[hsl(var(--accent))]" />
                </div>
                <p className="mt-1.5 text-[11px] text-foreground/50">
                  {view.perPageKb.toFixed(1)} KB per page
                  {view.domains != null && <> · roughly {fmtCount(view.domains)} sites at how deeply we read them</>}
                </p>
              </div>

              <div>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm text-foreground/70">
                    CC — a public web archive, for reference
                  </span>
                  <span className="font-mono text-sm">
                    <span className="text-lg font-semibold text-foreground/70">{fmtCount(view.theirs)}</span>
                    <span className="text-foreground/45"> pages</span>
                  </span>
                </div>
                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-foreground/10">
                  <div
                    className="h-full rounded-full bg-foreground/30 transition-all duration-500"
                    style={{ width: `${Math.max(view.theirsPct, 1.5)}%` }}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-foreground/50">{CC_LABEL} per page</p>
              </div>
            </div>

            <p className="mt-7 max-w-2xl text-sm leading-relaxed text-foreground/70">
              The same {cap.label} holds{" "}
              <span className="font-semibold text-foreground">{fmtCount(view.ours)}</span> pages here
              against <span className="font-semibold text-foreground">{fmtCount(view.theirs)}</span> at
              the reference rate. That is the whole reason holding the web is an ordinary business
              cost for us rather than the business itself.
            </p>
          </>
        )}
      </div>

      {/* The comparison is defensible only with this note attached. Removing it turns a fair
          reference point into a misleading claim about a public archive that is not a competitor. */}
      <div className="border-t hairline px-6 py-4">
        <p className="text-[11px] leading-relaxed text-foreground/45">
          Our figure is read live from the running index. CC is a large public web archive whose
          scale is published openly — 364.01 TiB across 2.14 billion pages, which works out at
          182.6 KB per page. It is not a like-for-like comparison and is not meant as one: an
          archive keeps fetched pages, which is a different job from keeping them searchable. It is
          used here because it is the one web-scale per-page storage figure anybody can check for
          themselves.
        </p>
      </div>
    </div>
  );
}
